import { closestBinarySearch } from '@surfline/web-common';
import { isFinite } from 'lodash';
import type { TidePoint } from 'types/tide';
import { oneMinuteSeconds } from './time';

const findPreviousTide = (tideReadings: Array<TidePoint>, closestIndex: number) => {
  let i = closestIndex - 1;
  while (i >= 0) {
    if (tideReadings[i] && tideReadings[i].type !== 'NORMAL') {
      return tideReadings[i];
    }
    i -= 1;
  }

  return tideReadings[closestIndex];
};

const findNextTide = (
  tideReadings: Array<TidePoint>,
  closestIndex: number,
  selectedTimestamp: number,
) => {
  const closestTimestamp = tideReadings[closestIndex]?.timestamp
    ? tideReadings[closestIndex].timestamp
    : selectedTimestamp;
  let i = closestTimestamp > selectedTimestamp ? closestIndex : closestIndex + 1;
  while (i < tideReadings.length) {
    if (tideReadings[i] && tideReadings[i].type !== 'NORMAL') {
      return tideReadings[i];
    }
    i += 1;
  }

  return tideReadings[closestIndex];
};

const getClosestTideDataPoint = (
  tideReadings: Array<TidePoint>,
  shouldInterpolate: boolean,
  closestTimestampToInterpolate: number,
  exactDataPointTimestampToFind?: number,
) => {
  const forceMatchTimestamp = !shouldInterpolate && exactDataPointTimestampToFind;
  const timestamp = forceMatchTimestamp
    ? exactDataPointTimestampToFind
    : closestTimestampToInterpolate;

  if (!isFinite(timestamp)) {
    return null;
  }
  try {
    const closestIndex = !forceMatchTimestamp
      ? closestBinarySearch(
          tideReadings,
          { timestamp },
          (tideReading: TidePoint) => tideReading.timestamp,
        )
      : tideReadings.findIndex(
          (tidePoint) => tidePoint.timestamp === exactDataPointTimestampToFind,
        );

    const closestTimestamp = tideReadings[closestIndex].timestamp;
    const closestUtcOffset = tideReadings[closestIndex].utcOffset;
    const t0 =
      closestTimestamp <= timestamp ? tideReadings[closestIndex] : tideReadings[closestIndex - 1];

    const t1 =
      closestTimestamp <= timestamp && !!tideReadings?.[closestIndex + 1]
        ? tideReadings[closestIndex + 1]
        : tideReadings[closestIndex];

    const timespan = timestamp - t0.timestamp;
    const heightIncrement = t1.height - t0.height;
    const timeIncrement = t1.timestamp - t0.timestamp;
    const interpolatedHeight =
      timeIncrement !== 0 ? t0.height + (timespan * heightIncrement) / timeIncrement : t0.height;
    const previousTide = findPreviousTide(tideReadings, closestIndex);
    const nextTide = findNextTide(tideReadings, closestIndex, timestamp);
    const isWithinOneMinute =
      Math.abs(timestamp - tideReadings[closestIndex].timestamp) < oneMinuteSeconds;
    return {
      timestamp,
      utcOffset: closestUtcOffset,
      previous: previousTide,
      current: {
        timestamp,
        height:
          isWithinOneMinute || !shouldInterpolate
            ? tideReadings[closestIndex].height
            : interpolatedHeight,
        utcOffset: closestUtcOffset,
        type:
          // only treat the current tide as High or Low when its within one minute of actual high or low
          tideReadings[closestIndex].type !== 'NORMAL' &&
          nextTide?.type !== tideReadings[closestIndex].type &&
          isWithinOneMinute
            ? tideReadings[closestIndex].type
            : 'NORMAL',
      },
      next: nextTide,
    };
  } catch (_err) {
    return null;
  }
};

export default getClosestTideDataPoint;
